/**
 * Where the platform's money is, and how it is moving.
 *
 * Every figure on this screen is one bucket from `GET /admin/settlements/overview`
 * — never two of them added together. That restraint is deliberate: the buckets
 * are the authoritative split, and a total assembled in the browser is a second
 * opinion that will disagree with the ledger the first time the backend's
 * definition shifts.
 *
 * It is also why the tiles are not labelled the way a generic finance dashboard
 * would label them. There is no bucket meaning "available", because money
 * becomes the seller's in two distinct steps — eligible at the end of the
 * return window, settled when the run credits it — and collapsing those into
 * one word is exactly the ambiguity an operator opens this page to resolve.
 *
 * Each stage keeps one colour everywhere on the page (see `tones.ts`), so the
 * tiles, the lifecycle and the status pills read as one system.
 */
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  Banknote,
  Clock,
  CornerUpLeft,
  Hourglass,
  Landmark,
  Percent,
  Truck,
  Wallet,
} from 'lucide-react';

import type { settlements } from '@terra/contracts';
import { Skeleton } from '@terra/ui';

import { compactInr, formatPaise } from '../../lib/money';

import { TONES, type StageTone } from './tones';

type Overview = settlements.AdminFinanceOverview;

const cardBase = 'border-border/60 bg-surface rounded-2xl border p-5 shadow-sm';

const orders = (n: number) => `${n} ${n === 1 ? 'order' : 'orders'}`;

function Tile({
  label,
  bucket,
  hint,
  icon: Icon,
  tone,
}: {
  label: string;
  bucket: settlements.FinanceBucket;
  hint: string;
  icon: typeof Wallet;
  tone: StageTone;
}) {
  const t = TONES[tone];
  return (
    <div className={`relative overflow-hidden rounded-2xl border p-5 shadow-sm ${t.card}`}>
      <span className={`absolute inset-x-0 top-0 h-1 ${t.solid}`} aria-hidden />
      <div className="flex items-start justify-between gap-3">
        <p className={`text-[11px] font-bold uppercase tracking-wider ${t.label}`}>{label}</p>
        <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${t.chip}`}>
          <Icon className="h-4 w-4" aria-hidden />
        </span>
      </div>
      <p className={`mt-1 text-[24px] font-bold tabular-nums leading-tight ${t.amount}`}>
        {formatPaise(bucket.paise)}
      </p>
      <p className="text-ink-muted mt-1 text-[12px] font-medium">
        {orders(bucket.count)} · {hint}
      </p>
    </div>
  );
}

function LoadingGrid() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-[132px] rounded-2xl" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[118px] rounded-2xl" />
        ))}
      </div>
    </div>
  );
}

export function MoneyPosition({
  overview,
  isLoading,
  isError,
  onRetry,
}: {
  overview: Overview | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  if (isLoading) return <LoadingGrid />;

  if (isError || !overview) {
    return (
      <div className={`${cardBase} text-center`}>
        <p className="text-ink text-[14px] font-bold">Unable to load the money position</p>
        <p className="text-ink-muted mx-auto mt-1 max-w-md text-[13px] font-medium">
          The settlement overview did not load. Everything below is unaffected and may still be
          usable.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="bg-ink hover:bg-ink/90 mt-4 h-9 rounded-lg px-4 text-[12.5px] font-bold text-white"
        >
          Retry
        </button>
      </div>
    );
  }

  const secondary: Array<{
    label: string;
    bucket: settlements.FinanceBucket;
    tone: StageTone;
    icon: typeof Wallet;
  }> = [
    { label: 'Customer payments received', bucket: overview.received, tone: 'sky', icon: Landmark },
    {
      label: 'Platform commission',
      bucket: overview.platformCommission,
      tone: 'indigo',
      icon: Percent,
    },
    {
      label: 'Refunded to customers',
      bucket: overview.refunded,
      tone: 'rose',
      icon: CornerUpLeft,
    },
  ];

  return (
    <motion.section
      aria-label="Money position"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="space-y-4"
    >
      {/* The headline: money whose return window has closed and which the next
          run will credit to sellers. The one number that answers "what am I
          about to pay out?" */}
      <div className="relative flex flex-wrap items-center justify-between gap-4 overflow-hidden rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 via-white to-white p-6 shadow-sm">
        <span className="absolute inset-y-0 left-0 w-1.5 bg-violet-500" aria-hidden />
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-800">
            Ready to settle
          </p>
          <p className="mt-1.5 text-[34px] font-bold tabular-nums leading-none text-violet-950">
            {formatPaise(overview.eligible.paise)}
          </p>
          <p className="text-ink-muted mt-2 text-[13px] font-medium">
            {orders(overview.eligible.count)} past their return window, waiting for the next
            settlement run.
          </p>
        </div>
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-violet-100 text-violet-700">
          <Hourglass className="h-6 w-6" aria-hidden />
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          label="In return window"
          bucket={overview.inReturnWindow}
          hint="not the seller's yet"
          icon={Clock}
          tone="amber"
        />
        <Tile
          label="Awaiting COD remittance"
          bucket={overview.awaitingCodRemittance}
          hint="cash still with the courier"
          icon={Truck}
          tone="orange"
        />
        <Tile
          label="Settled to sellers"
          bucket={overview.settledNet}
          hint="credited this period"
          icon={Wallet}
          tone="emerald"
        />
        <Tile
          label="Paid out"
          bucket={overview.payoutsReleased}
          hint="transferred this period"
          icon={Banknote}
          tone="teal"
        />
      </div>

      {/* What came in, what the platform kept, and what went back to customers.
          Kept apart from the tiles above: none of it is money owed to a seller,
          and mixing marketplace revenue into a payout figure is how a payout
          gets over-reported. */}
      <div className="grid gap-4 sm:grid-cols-3">
        {secondary.map((r) => {
          const t = TONES[r.tone];
          return (
            <div
              key={r.label}
              className={`flex items-center gap-3.5 rounded-2xl border px-4 py-3.5 ${t.card}`}
            >
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${t.chip}`}>
                <r.icon className="h-4 w-4" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className={`text-[11px] font-bold uppercase tracking-wider ${t.label}`}>
                  {r.label}
                </p>
                <p className={`text-[17px] font-bold tabular-nums leading-tight ${t.amount}`}>
                  {formatPaise(r.bucket.paise)}
                </p>
                <p className="text-ink-muted text-[11.5px] font-medium">{orders(r.bucket.count)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}

/**
 * The same money, as a pipeline.
 *
 * The stages are named after what the backend actually tracks rather than a
 * generic payments funnel: there is no separate "captured" bucket, because
 * `received` already means captured online payments plus collected cash, and
 * splitting it visually would invent a distinction the data does not make.
 */
export function PaymentLifecycle({
  overview,
  isLoading,
}: {
  overview: Overview | undefined;
  isLoading: boolean;
}) {
  if (isLoading) return <Skeleton className="h-[120px] rounded-2xl" />;
  if (!overview) return null;

  const stages: Array<{
    label: string;
    bucket: settlements.FinanceBucket;
    note: string;
    tone: StageTone;
  }> = [
    {
      label: 'Customer paid',
      bucket: overview.received,
      note: 'captured or collected',
      tone: 'sky',
    },
    {
      label: 'In return window',
      bucket: overview.inReturnWindow,
      note: 'delivered, returnable',
      tone: 'amber',
    },
    {
      label: 'Awaiting COD cash',
      bucket: overview.awaitingCodRemittance,
      note: 'courier holds it',
      tone: 'orange',
    },
    { label: 'Ready to settle', bucket: overview.eligible, note: 'window closed', tone: 'violet' },
    {
      label: 'Settled to sellers',
      bucket: overview.settledNet,
      note: 'after fees',
      tone: 'emerald',
    },
    {
      label: 'Paid out',
      bucket: overview.payoutsReleased,
      note: 'bank transfer done',
      tone: 'teal',
    },
  ];

  const legend: Array<[StageTone, string]> = [
    ['sky', 'Money in'],
    ['amber', 'Waiting'],
    ['violet', 'Ready'],
    ['emerald', 'Done'],
  ];

  return (
    <section
      aria-label="Payment lifecycle"
      className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
          Payment lifecycle
        </p>
        <ul className="text-ink-muted flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[11.5px] font-semibold">
          {legend.map(([tone, label]) => (
            <li key={label} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${TONES[tone].solid}`} aria-hidden />
              {label}
            </li>
          ))}
        </ul>
      </div>
      <ol className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        {stages.map((s, i) => {
          const t = TONES[s.tone];
          return (
            <li key={s.label} className="flex flex-1 items-center gap-3">
              <div
                className={`relative min-w-0 flex-1 self-stretch overflow-hidden rounded-xl border p-3.5 pl-4 ${t.card}`}
              >
                <span className={`absolute inset-y-0 left-0 w-1 ${t.solid}`} aria-hidden />
                <p
                  className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${t.label}`}
                >
                  <span
                    className={`grid h-4 w-4 shrink-0 place-items-center rounded-full text-[9px] text-white ${t.solid}`}
                  >
                    {i + 1}
                  </span>
                  {s.label}
                </p>
                <p className={`mt-1 text-[16px] font-bold tabular-nums ${t.amount}`}>
                  {compactInr(s.bucket.paise)}
                </p>
                <p className="text-ink-muted text-[11.5px] font-medium">
                  {orders(s.bucket.count)} · {s.note}
                </p>
              </div>
              {i < stages.length - 1 && (
                <ArrowRight
                  className="text-ink-muted/40 hidden h-4 w-4 shrink-0 lg:block"
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
      <p className="text-ink-muted mt-3 flex items-start gap-1.5 text-[11.5px] font-medium">
        <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
        Each figure is one settlement bucket for the selected period. Stages are not subsets of one
        another, so they do not sum to the total received.
      </p>
    </section>
  );
}
